import * as React from 'react';
import { Vegobjekttype } from '@/domain/vegobjekter/Vegobjekttype';
import { VegobjekttypeState } from '@/state';
import { ColorIndex, colorsEqual } from '@/utils/ColorHelper';
import {
    getDefaultCategoryColor,
    getDefaultVegobjekttypeColor,
    hasDefaultCategoryColors,
} from '@/utils/ColorPreferences';
import { ColorMenu, ColorMenuAction, ColorMenuActionRow } from './ColorMenu';

interface Props {
    vegobjekttype: Vegobjekttype;
    vegobjekttypeData: VegobjekttypeState;
    anchorPoint: { x: number; y: number };
    isOpen: boolean;
    onClose: () => void;
    onSetVegobjekttypeColor: (
        votState: VegobjekttypeState,
        color: ColorIndex,
        saveAsDefault?: boolean
    ) => void;
    onResetVegobjekttypeColor: (votState: VegobjekttypeState) => void;
    onSetAllCategoryColors: (
        votState: VegobjekttypeState,
        color: ColorIndex,
        saveAsDefault?: boolean
    ) => void;
    onResetAllCategoryColors: (votState: VegobjekttypeState) => void;
}

export const VegobjekttypeColorMenu: React.FC<Props> = ({
    vegobjekttype,
    vegobjekttypeData,
    anchorPoint,
    isOpen,
    onClose,
    onSetVegobjekttypeColor,
    onResetVegobjekttypeColor,
    onSetAllCategoryColors,
    onResetAllCategoryColors,
}) => {
    const hasCategories = vegobjekttypeData.categoryStates.length > 0;
    const savedDefaultColor =
        vegobjekttypeData.typeId === null
            ? null
            : getDefaultVegobjekttypeColor(vegobjekttypeData.typeId);
    const hasSavedDefaultColor = savedDefaultColor !== null;
    const hasSavedCategoryColors =
        vegobjekttypeData.typeId !== null &&
        hasDefaultCategoryColors(vegobjekttypeData.typeId);
    const hasManualCategoryColor = vegobjekttypeData.categoryStates.some(
        categoryState => categoryState.colorIsManual
    );
    const canResetObject = hasSavedDefaultColor || vegobjekttypeData.colorIsManual;
    const canResetCategories = hasSavedCategoryColors || hasManualCategoryColor;
    const showResetActions = canResetObject || canResetCategories;
    const objectActions: ColorMenuAction[] = [
        {
            title: `Bruk farge for ${vegobjekttype.navn}`,
            icon: (
                <i className="material-icons" aria-hidden="true">
                    check
                </i>
            ),
            onClick: color => onSetVegobjekttypeColor(vegobjekttypeData, color),
            className: 'c-color-menu__action--primary',
            disabledTitle: `Fargen er allerede valgt for ${vegobjekttype.navn}`,
            disabled: color => colorsEqual(color, vegobjekttypeData.color),
        },
        {
            title: `Bruk som standardfarge for ${vegobjekttype.navn}`,
            icon: (
                <i className="material-icons" aria-hidden="true">
                    star
                </i>
            ),
            onClick: color => onSetVegobjekttypeColor(vegobjekttypeData, color, true),
            disabledTitle: `Fargen er allerede standardfarge for ${vegobjekttype.navn}`,
            disabled: color => colorsEqual(color, savedDefaultColor),
        },
    ];

    if (showResetActions) {
        objectActions.push({
            title: `Tilbakestill ${vegobjekttype.navn} til automatisk farge`,
            icon: (
                <i className="material-icons" aria-hidden="true">
                    refresh
                </i>
            ),
            onClick: () => onResetVegobjekttypeColor(vegobjekttypeData),
            disabled: !canResetObject,
        });
    }

    const actionRows: ColorMenuActionRow[] = [
        {
            title: `Vegobjekttype: ${vegobjekttype.navn}`,
            icon: (
                <i className="material-icons" aria-hidden="true">
                    {vegobjekttype.isLine() ? 'timeline' : 'room'}
                </i>
            ),
            actions: objectActions,
        },
    ];

    if (hasCategories) {
        const categoryActions: ColorMenuAction[] = [
            {
                title: 'Bruk farge for alle kategorier',
                disabledTitle: 'Fargen er allerede valgt for alle kategorier',
                icon: (
                    <i className="material-icons" aria-hidden="true">
                        check
                    </i>
                ),
                onClick: color => onSetAllCategoryColors(vegobjekttypeData, color),
                className: 'c-color-menu__action--primary',
                disabled: color =>
                    vegobjekttypeData.categoryStates.every(categoryState =>
                        colorsEqual(color, categoryState.color)
                    ),
            },
            {
                title: 'Bruk som standardfarge for alle kategorier',
                disabledTitle: 'Fargen er allerede standardfarge for alle kategorier',
                icon: (
                    <i className="material-icons" aria-hidden="true">
                        star
                    </i>
                ),
                onClick: color => onSetAllCategoryColors(vegobjekttypeData, color, true),
                disabled: color =>
                    vegobjekttypeData.categoryStates.every(
                        categoryState =>
                            vegobjekttypeData.typeId !== null &&
                            colorsEqual(
                                color,
                                getDefaultCategoryColor(
                                    vegobjekttypeData.typeId,
                                    categoryState.filters
                                )
                            )
                    ),
            },
        ];

        if (showResetActions) {
            categoryActions.push({
                title: 'Tilbakestill alle kategorier til automatiske farger',
                icon: (
                    <i className="material-icons" aria-hidden="true">
                        refresh
                    </i>
                ),
                onClick: () => onResetAllCategoryColors(vegobjekttypeData),
                disabled: !canResetCategories,
            });
        }

        actionRows.push({
            title: 'Alle kategorier',
            icon: (
                <i
                    className="o-icon-category c-color-menu__category-icon"
                    aria-hidden="true"
                />
            ),
            actions: categoryActions,
        });
    }

    return (
        <ColorMenu
            title={`Farge for ${vegobjekttype.navn}`}
            currentColor={vegobjekttypeData.color}
            anchorPoint={anchorPoint}
            isOpen={isOpen}
            onClose={onClose}
            actionRows={actionRows}
        />
    );
};
